import * as React from 'react';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { PlanTable, ModuleName, PlanTableWrapper, AvailabilityPill, PlanHeaderGrid, PlanHeaderSpacer, CurrentPlanPill, NewPlanPill, PaymentInfoIcon, PlanMetaNote } from './styled';
import ToolTip from '../../components/ToolTip';
import { SubscriptionPlanDefinition, SubscriptionFeatureDefinition } from '../../models';
import { formatCurrency, formatDateLocalized } from './utils';

export interface PlanComparisonProps {
    plans: Record<string, SubscriptionPlanDefinition>;
    features: SubscriptionFeatureDefinition[];
    selectedPlan: string;
    onSelectPlan: (plan: string) => void;
    billingPeriod: 'monthly' | 'yearly';
    planOrder: string[];
    currentPlan?: string | null;
    lastPaymentDate?: Date | string | null;
    paymentValidityDate?: Date | string | null;
}

const PlanComparison: React.FC<PlanComparisonProps> = ({
    plans,
    features,
    selectedPlan,
    onSelectPlan,
    billingPeriod,
    planOrder,
    currentPlan,
    lastPaymentDate,
    paymentValidityDate
}) => {
    const t = useTranslations();

    const isCurrentPlan = (planKey: string) => {
        if (!currentPlan) return false;
        return currentPlan.toLowerCase() === planKey.toLowerCase();
    };

    const resolvePlan = (planType: string) => plans[planType] || plans[planType.toLowerCase()];

    const orderedPlanKeys = planOrder.length > 0 ? planOrder : Object.keys(plans);
    const quotaRows: Array<'totalUsers'> = ['totalUsers'];

    if (!orderedPlanKeys.length) {
        return <div>{t(TK.loading)}</div>;
    }

    const filteredFeatures = features.filter((feature) => {
        const key = feature.key.toLowerCase();
        return key !== 'totalusers' && key !== 'totalemails';
    });

    const selectedColumn = (planType: string) => (selectedPlan.toLowerCase() === planType.toLowerCase() ? 'selected' : undefined);
    const renderAvailability = (isIncluded: boolean) => (
        <AvailabilityPill className={isIncluded ? 'available' : 'unavailable'}>
            {isIncluded ? '✓' : '–'}
        </AvailabilityPill>
    );

    return (
        <PlanTableWrapper>
            <div className="table-scroll">
                <PlanHeaderGrid $planCount={orderedPlanKeys.length}>
                    <PlanHeaderSpacer />
                    {orderedPlanKeys.map((planKey) => {
                        const plan = resolvePlan(planKey);
                        const pricePerMonth = billingPeriod === 'yearly'
                            ? plan?.pricePerMonthBilledAnnually ?? plan?.pricePerMonthBilledMonthly ?? 0
                            : plan?.pricePerMonthBilledMonthly ?? plan?.pricePerMonthBilledAnnually ?? 0;

                        if (!plan) return null;

                        const isCurrent = isCurrentPlan(planKey);

                        const paymentInfoContent = (lastPaymentDate || paymentValidityDate) ? (
                            <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                                {lastPaymentDate && (
                                    <div><strong>{t(TK.lastPaymentDate)}:</strong> {formatDateLocalized(lastPaymentDate, t(TK.notAvailable))}</div>
                                )}
                                {paymentValidityDate && (
                                    <div><strong>{t(TK.validUntil)}:</strong> {formatDateLocalized(paymentValidityDate, t(TK.notAvailable))}</div>
                                )}
                            </div>
                        ) : null;

                        const isSelected = selectedPlan === planKey;
                        const isNewPlan = isSelected && !isCurrent;

                        return (
                            <button
                                key={planKey}
                                className={`plan-card ${selectedColumn(planKey) ?? ''} ${isCurrent ? 'current' : ''}`}
                                onClick={() => onSelectPlan(planKey)}
                                type="button"
                                aria-pressed={isSelected}
                            >
                                {isCurrent && (
                                    <CurrentPlanPill>
                                        {t(TK.current)}
                                        {paymentInfoContent && (
                                            <>
                                                <PaymentInfoIcon id={`payment-info-${planKey}`}>i</PaymentInfoIcon>
                                                <ToolTip target={`payment-info-${planKey}`} toolTipContent={paymentInfoContent} />
                                            </>
                                        )}
                                    </CurrentPlanPill>
                                )}
                                {isNewPlan && (
                                    <NewPlanPill>
                                        {t(TK.newPlan)}
                                    </NewPlanPill>
                                )}
                                <div className="plan-card__title">{plan.name}</div>
                                <div className="plan-card__subtitle">{plan.description}</div>
                                <div className="plan-card__price">
                                    {formatCurrency(pricePerMonth)}
                                    <span className="plan-card__per"> / {t(TK.month)}</span>
                                </div>
                                <PlanMetaNote>
                                    {billingPeriod === 'yearly' ? t(TK.billedAnnually) : t(TK.billedMonthly)}
                                </PlanMetaNote>
                            </button>
                        );
                    })}
                </PlanHeaderGrid>
                <PlanTable $planCount={orderedPlanKeys.length}>
                    <colgroup>
                        <col className="feature-col" />
                        {orderedPlanKeys.map((planKey) => (
                            <col key={planKey} className="plan-col" />
                        ))}
                    </colgroup>
                    <tbody>
                        {quotaRows.map((key) => {
                            const tooltipId = 'total-users-label';
                            const label = t(TK.totalUsers);
                            const tooltip = t(TK.usersIncluded);

                            return (
                                <tr key={key}>
                                    <td>
                                        <ModuleName id={tooltipId}>
                                            {label}
                                        </ModuleName>
                                        <ToolTip target={tooltipId} toolTipContent={tooltip} />
                                    </td>
                                    {orderedPlanKeys.map(planKey => {
                                        const plan = resolvePlan(planKey);
                                        return (
                                            <td key={`${key}-${planKey}`} className={selectedColumn(planKey)} data-label={plan?.name || planKey}>
                                                {plan?.includedUsers ?? '-'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}

                        {/* Feature rows */}
                        {filteredFeatures.map((feature, idx) => {
                            const tooltipId = `feature-${feature.key}-${idx}`;

                            return (
                                <tr key={feature.key}>
                                    <td>
                                        <ModuleName id={tooltipId}>
                                            {feature.name}
                                        </ModuleName>
                                        <ToolTip
                                            target={tooltipId}
                                            toolTipContent={feature.description}
                                        />
                                    </td>
                                    {orderedPlanKeys.map(planKey => {
                                        const plan = resolvePlan(planKey);
                                        const keyLower = (plan?.type || planKey).toLowerCase();
                                        const isIncluded = keyLower === 'basic'
                                            ? feature.includedInBasic
                                            : keyLower === 'pro'
                                                ? feature.includedInPro
                                                : feature.includedInProPlus;
                                        return (
                                            <td key={`${feature.key}-${planKey}`} className={selectedColumn(planKey)} data-label={plan?.name || planKey}>
                                                {renderAvailability(isIncluded)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </PlanTable>
            </div>
        </PlanTableWrapper>
    );
};

export default PlanComparison;
